import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "material-ui/styles";

// 子コンポーネント
import DepartmentHints from "./DepartmentHints";
import DepartmentPieSection from "./DepartmentPieSection";

// ================================
// 定数：カテゴリ名を一元管理
// ================================
export const CATEGORY_MAP = {
  scenario: "顧客との関わり方のタイプ",
  theme: "顧客が求めていた主なテーマ",
  interest: "提案への関心度",
  success: "うまくいったポイント",
  obstacles: "進行を妨げている要因",
};

export const PERSONALITY_MAP = {
  tone: "話し方のトーン",
  speakingStyle: "話し方のスタイル",
  interactionPattern: "会話の進め方",
  relationship: "関係性スタイル",
  openness: "新しい提案へのオープンさ",
  change: "変化へのスタンス",
  risk: "リスクへの姿勢",
};

const styles = () => ({
  tagsRoot: {
    marginTop: "10px",
    marginLeft: "-4px",
  },
  categoryBlock: {
    marginBottom: "8px",
    padding: "20px",
  },
  categoryTitle: {
    fontSize: "12px",
    fontWeight: "bold",
    marginBottom: "15px",
    color: "#333",
  },

  // セクション見出し（グループのタイトル）
  graphsSectionTitle: {
    color: "#181819",
    fontSize: "13px",
    boxSizing: "border-box",
    fontWeight: "bold",
    borderBottom: "1px solid #D8DBDF",
    paddingBottom: "10px",
    marginBottom: "10px",
  },

  // 2列グリッド用
  graphsGrid: {
    display: "flex",
    flexWrap: "wrap",
    marginLeft: "4px",
    marginRight: "-8px",
  },
  graphItem: {
    width: "50%",
    boxSizing: "border-box",
    paddingRight: "15px",
    marginBottom: "15px",
  },

  // 集計グラフ用
  tagStatsList: {},

  // 円グラフ＋凡例用（DepartmentPieSection から利用）
  pieRow: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: "6px",
  },
  pieChart: {
    width: "70px",
    height: "70px",
    borderRadius: "50%",
    flexShrink: 0,
    marginRight: "8px",
    border: "1px solid #E0E0E0",
  },
  legendList: {
    flexGrow: 1,
  },
  legendRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: "2px",
    fontSize: "11px",
    width: "145px",
    flexShrink: 0,
  },
  legendColorBox: {
    width: "10px",
    height: "10px",
    borderRadius: "2px",
    marginRight: "4px",
    flexShrink: 0,
  },
  legendLabelContainer: {
    flexGrow: 1,
    minWidth: 0,
    marginRight: "4px",
    overflow: "hidden",
  },
  legendLabelText: {
    display: "block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "#333",
  },
  legendPercent: {
    marginLeft: "4px",
    color: "#666",
    flexShrink: 0,
    minWidth: "32px",
    textAlign: "right",
  },

  // 攻略ヒント用
  hintsBlock: {
    padding: "20px",
  },
  hintsTitle: {
    color: "#181819",
    fontSize: "13px",
    boxSizing: "border-box",
    fontWeight: "bold",
    borderBottom: "1px solid #D8DBDF",
    paddingBottom: "10px",
    marginBottom: "10px",
  },
  hintsListWrapper: {
    marginTop: "6px",
    marginBottom: "8px",
    padding: "6px 8px",
    borderRadius: "4px",
    backgroundColor: "#FFF8E8",
    border: "1px solid #F5D08A",
  },
  hintsList: {
    paddingLeft: "14px",
    margin: 0,
  },
  hintItem: {
    fontSize: "11px",
    color: "#5A4A20",
    marginBottom: "2px",
  },
});

class CallAnalysisTags extends React.Component {
  /**
   * 1件の分析からカテゴリごとにタグ配列を取得
   * （案件軸の分布：シナリオ / テーマ / 関心度 / 成功要因 / 障壁）
   */
  extractCategoryTags = (analysis) => {
    const categories = {};
    // 定義済みの表示ラベルをそのままキーに
    Object.values(CATEGORY_MAP).forEach((label) => {
      categories[label] = [];
    });

    if (!analysis) return categories;

    const cls = analysis.classification || {};
    const customer = analysis.customer_profile || {};

    if (cls && cls.scenario_type) {
      categories[CATEGORY_MAP.scenario].push(cls.scenario_type);
    }
    if (cls && cls.primary_theme) {
      categories[CATEGORY_MAP.theme].push(cls.primary_theme);
    }
    if (customer && customer.interest_level && customer.interest_level.label) {
      categories[CATEGORY_MAP.interest].push(customer.interest_level.label);
    }
    if (cls && Array.isArray(cls.success_driver_tags)) {
      categories[CATEGORY_MAP.success].push(...cls.success_driver_tags);
    }
    if (cls && Array.isArray(cls.obstacle_categories)) {
      categories[CATEGORY_MAP.obstacles].push(...cls.obstacle_categories);
    }

    return categories;
  };

  /**
   * 複数の callAnalyses からカテゴリごとに { tag: count } を集約
   * （案件軸の傾向）
   */
  aggregateCategoryStats = (callAnalyses) => {
    const aggregated = {};
    Object.values(CATEGORY_MAP).forEach((label) => {
      aggregated[label] = {};
    });

    callAnalyses.forEach((row) => {
      const analysis = row.ai_analysis || row;
      const extracted = this.extractCategoryTags(analysis);

      Object.keys(aggregated).forEach((categoryLabel) => {
        const tags = extracted[categoryLabel] || [];
        const agg = aggregated[categoryLabel];

        tags.forEach((val) => {
          if (!val) return;
          if (agg[val] == null) agg[val] = 1;
          else agg[val] += 1;
        });
      });
    });

    return aggregated;
  };

  /**
   * 複数の callAnalyses から「人柄・話し方」系の分布を集約
   * （部門に所属するキーマンのパーソナリティ的な傾向）
   */
  aggregatePersonalityStats = (callAnalyses) => {
    const aggregated = {};
    Object.values(PERSONALITY_MAP).forEach((label) => {
      aggregated[label] = {};
    });

    callAnalyses.forEach((row) => {
      const analysis = row.ai_analysis || row;
      const customer = analysis.customer_profile || {};
      const comm = customer.communication_style || {};
      const persona = customer.personality_profile || {};

      const mapping = {
        [PERSONALITY_MAP.tone]: comm.tone,
        [PERSONALITY_MAP.speakingStyle]: comm.speaking_style,
        [PERSONALITY_MAP.interactionPattern]: comm.interaction_pattern,
        [PERSONALITY_MAP.relationship]: persona.relationship_style,
        [PERSONALITY_MAP.openness]: persona.openness_to_new_ideas,
        [PERSONALITY_MAP.change]: persona.stability_or_change_preference,
        [PERSONALITY_MAP.risk]: customer.risk_attitude,
      };

      Object.keys(mapping).forEach((label) => {
        const val = mapping[label];
        if (!val) return;
        if (aggregated[label][val] == null) aggregated[label][val] = 1;
        else aggregated[label][val] += 1;
      });
    });

    return aggregated;
  };

  /**
   * 部門としての攻略ヒント（playbook ＋ opportunity）を集約
   */
  mergeHints = (callAnalyses) => {
    const recommended = {};
    const opportunities = {};

    callAnalyses.forEach((row) => {
      const analysis = row.ai_analysis || row;
      const feedback = analysis.call_feedback || {};

      if (Array.isArray(feedback.recommended_playbook)) {
        feedback.recommended_playbook.forEach((item) => {
          if (!item) return;
          recommended[item] = true;
        });
      }

      if (Array.isArray(feedback.opportunity_insights)) {
        feedback.opportunity_insights.forEach((item) => {
          if (!item) return;
          opportunities[item] = true;
        });
      }
    });

    return {
      recommended: Object.keys(recommended),
      opportunities: Object.keys(opportunities),
    };
  };

  render() {
    const { classes, callAnalyses } = this.props;

    if (!callAnalyses || callAnalyses.length === 0) return null;

    const categoryStats = this.aggregateCategoryStats(callAnalyses);
    const personalityStats = this.aggregatePersonalityStats(callAnalyses);
    const hints = this.mergeHints(callAnalyses);

    return (
      <div className={classes.tagsRoot}>
        <DepartmentHints classes={classes} hints={hints} />

        <DepartmentPieSection
          classes={classes}
          title="この部門のキーマンの人柄・話し方の傾向"
          stats={personalityStats}
        />

        <DepartmentPieSection
          classes={classes}
          title="この部門の商談・ニーズの傾向"
          stats={categoryStats}
        />
      </div>
    );
  }
}

CallAnalysisTags.propTypes = {
  classes: PropTypes.object.isRequired,
  callAnalyses: PropTypes.array,
};

CallAnalysisTags.defaultProps = {
  callAnalyses: [],
};

export default withStyles(styles)(CallAnalysisTags);



// WEBPACK FOOTER //
// ./src/components/ExecutiveList/dialogs/ExecDetailItemDialog/parts/CallAnalysisTags.jsx